import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlarmClock, HandCoins, Info, Loader2 } from 'lucide-react';
import type { MyInvoiceItem, MyInvoiceStatus } from '@ip/shared-types';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { cn } from '@/lib/utils';
import { formatDateMedium, todayYmd } from '@/lib/hr';
import { t } from '@/i18n';

const STATUS_CHIP: Record<MyInvoiceStatus, string> = {
  received: 'border-zinc-400/40 bg-zinc-400/10 text-zinc-600 dark:text-zinc-400',
  passed_for_payment: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  paid: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

function formatAmount(gross: number | null, currency: string | null): string {
  if (gross === null) return '—';
  return `${gross.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? 'PLN'}`;
}

/**
 * B2B self-service: the contractor's own invoices with the contractual
 * 14-days-from-receipt due date, the employee-facing payment progress and
 * the two actions (earlier-transfer request, funds-not-received alarm).
 */
export function MyInvoicesView({ nip }: { nip: string }) {
  const { myInvoices, requestEarlyPayment, fundsAlarm } = useHrApi();
  const [rows, setRows] = useState<MyInvoiceItem[] | null>(null);

  const [earlyFor, setEarlyFor] = useState<MyInvoiceItem | null>(null);
  const [earlyDate, setEarlyDate] = useState('');
  const [earlyNote, setEarlyNote] = useState('');
  const [busy, setBusy] = useState(false);

  const today = todayYmd();

  useEffect(() => {
    let mounted = true;
    myInvoices(nip)
      .then((r) => { if (mounted) setRows(r); })
      .catch((error) => {
        if (mounted) setRows([]);
        if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
      });
    return () => { mounted = false; };
  }, [nip, myInvoices]);

  const handleEarlySend = async () => {
    if (!earlyFor || !earlyDate) return;
    setBusy(true);
    try {
      await requestEarlyPayment(nip, earlyFor.invoiceId, {
        requestedDate: earlyDate,
        note: earlyNote.trim() || undefined,
      });
      toast.success(t('hr.myInvoices.earlyPaymentSent'));
      setEarlyFor(null);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleFundsAlarm = async (row: MyInvoiceItem) => {
    try {
      await fundsAlarm(nip, row.invoiceId);
      toast.success(t('hr.myInvoices.fundsAlarmSent'));
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    }
  };

  if (rows === null) return <Skeleton className="h-72 w-full" />;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <Info className="size-3.5 shrink-0" />
        {t('hr.myInvoices.termInfo')}
      </p>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('hr.myInvoices.colNumber')}</TableHead>
              <TableHead>{t('hr.myInvoices.colReceived')}</TableHead>
              <TableHead>{t('hr.myInvoices.colDue')}</TableHead>
              <TableHead className="text-right">{t('hr.myInvoices.colAmount')}</TableHead>
              <TableHead>{t('hr.myInvoices.colStatus')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  {t('hr.myInvoices.empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const overdue = row.status !== 'paid' && row.dueDate < today;
                return (
                  <TableRow key={row.invoiceId}>
                    <TableCell className="py-2 font-medium">{row.invoiceNumber ?? '—'}</TableCell>
                    <TableCell className="py-2">{formatDateMedium(row.receivedDate)}</TableCell>
                    <TableCell className={cn('py-2', overdue && 'font-medium text-rose-600 dark:text-rose-400')}>
                      {formatDateMedium(row.dueDate)}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums">
                      {formatAmount(row.gross, row.currency)}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline" className={cn('font-normal', STATUS_CHIP[row.status])}>
                        {t(`hr.myInvoices.status.${row.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex justify-end gap-1.5">
                        {row.status !== 'paid' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            onClick={() => {
                              setEarlyDate('');
                              setEarlyNote('');
                              setEarlyFor(row);
                            }}
                          >
                            <HandCoins className="size-3.5" />
                            {t('hr.myInvoices.earlyPayment')}
                          </Button>
                        )}
                        {row.status !== 'received' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="h-7 text-rose-600 dark:text-rose-400">
                                <AlarmClock className="size-3.5" />
                                {t('hr.myInvoices.fundsAlarm')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t('hr.myInvoices.fundsAlarmConfirm', { invoice: row.invoiceNumber ?? '—' })}
                                </AlertDialogTitle>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleFundsAlarm(row)}
                                >
                                  {t('hr.myInvoices.fundsAlarm')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!earlyFor} onOpenChange={(open) => !open && setEarlyFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('hr.myInvoices.earlyPaymentTitle', { invoice: earlyFor?.invoiceNumber ?? '—' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="early-date">{t('hr.myInvoices.earlyPaymentDate')}</Label>
              <Input
                id="early-date"
                type="date"
                min={today}
                value={earlyDate}
                onChange={(e) => setEarlyDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="early-note">{t('hr.myInvoices.earlyPaymentNote')}</Label>
              <Textarea
                id="early-note"
                rows={3}
                maxLength={500}
                value={earlyNote}
                onChange={(e) => setEarlyNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEarlyFor(null)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy || !earlyDate} onClick={handleEarlySend}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('hr.myInvoices.earlyPaymentSend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
